import React, { useRef, useState } from 'react';
import { uploadToR2 } from '../../utils/r2Upload';
import { TranscriptionFields, useWhisperModels } from '../ShortsSetupFields';
import ExistingVideoPicker, { ExistingVideo } from '../ExistingVideoPicker';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface LatestJob {
  status: string;
  stage: string;
  progress: number;
}

interface KineticVideoUploadProps {
  projectId: string;
  workspaceId: string;
  videoId: string | null;
  latestJob: LatestJob | null;
  onConfirmed: () => void;
}

type Phase = 'idle' | 'uploading' | 'confirming' | 'error';
type Source = 'upload' | 'existing';

const stageLabel = (stage: string) => `⚙️ ${stage.charAt(0).toUpperCase()}${stage.slice(1)}...`;

const KineticVideoUpload: React.FC<KineticVideoUploadProps> = ({ projectId, workspaceId, videoId, latestJob, onConfirmed }) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [uploadPercent, setUploadPercent] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [language, setLanguage] = useState('auto');
  // The caption pass is a Whisper run, so it takes the same Language + model inputs as every
  // other shorts / caption entry point.
  const { models: whisperModels, model: whisperModel, setModel: setWhisperModel } = useWhisperModels(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [source, setSource] = useState<Source>('upload');
  const [selectedVideo, setSelectedVideo] = useState<ExistingVideo | null>(null);
  const [reuseTranscript, setReuseTranscript] = useState(true);

  const handleFile = async (file: File) => {
    setPhase('uploading');
    setErrorMsg(null);
    setUploadPercent(0);

    try {
      const initRes = await fetch(`${API_BASE}/api/projects/${projectId}/videos/init`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: file.name, fileSize: file.size, language }),
      });
      if (!initRes.ok) {
        const data = await initRes.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to start upload');
      }
      const { videoId: newVideoId, presignedUrl } = await initRes.json();

      await uploadToR2(presignedUrl, file, (progress) => setUploadPercent(progress.percent));

      setPhase('confirming');
      const confirmRes = await fetch(
        `${API_BASE}/api/projects/${projectId}/caption-workspaces/${workspaceId}/videos/confirm`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ videoId: newVideoId, whisperModel }),
        }
      );
      if (!confirmRes.ok) {
        const data = await confirmRes.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to confirm upload');
      }

      onConfirmed();
    } catch (e: any) {
      setErrorMsg(e.message || 'Upload failed');
      setPhase('error');
    }
  };

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  const handleUseExisting = async () => {
    if (!selectedVideo) return;
    setPhase('confirming');
    setErrorMsg(null);

    try {
      const confirmRes = await fetch(
        `${API_BASE}/api/projects/${projectId}/caption-workspaces/${workspaceId}/videos/confirm`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            videoId: selectedVideo.id,
            whisperModel,
            reuseTranscript: selectedVideo.has_transcript && reuseTranscript,
          }),
        }
      );
      if (!confirmRes.ok) {
        const data = await confirmRes.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to confirm video');
      }

      onConfirmed();
    } catch (e: any) {
      setErrorMsg(e.message || 'Failed to use this video');
      setPhase('error');
    }
  };

  const transcriptionInputs = (
    <div className="kc-upload-inputs">
      <TranscriptionFields
        language={language}
        onLanguageChange={setLanguage}
        model={whisperModel}
        onModelChange={setWhisperModel}
        models={whisperModels}
        helpText="Used only for this caption job's transcription."
      />
    </div>
  );

  // Already linked to a video and its job is still running — show processing state.
  if (videoId && latestJob && !['done', 'completed', 'failed'].includes(latestJob.status)) {
    return (
      <div className="kc-upload-widget">
        <div className="spinner" />
        <p className="kc-upload-status">{stageLabel(latestJob.stage || latestJob.status)}</p>
      </div>
    );
  }

  if (videoId && latestJob?.status === 'failed') {
    return (
      <div className="kc-upload-widget">
        <p className="kc-upload-status kc-upload-status--error">⚠️ Processing failed.</p>
        <button className="btn-sm btn-accent" onClick={() => fileInputRef.current?.click()}>Retry Upload</button>
        <input ref={fileInputRef} type="file" accept="video/*" hidden onChange={onFileInputChange} />
      </div>
    );
  }

  if (videoId) {
    // Video linked, job terminal/unknown yet — parent will swap this out once captions load.
    return (
      <div className="kc-upload-widget">
        <div className="spinner" />
        <p className="kc-upload-status">Loading...</p>
      </div>
    );
  }

  if (phase === 'uploading') {
    return (
      <div className="kc-upload-widget">
        <div className="kc-upload-progress">
          <div className="kc-upload-bar" style={{ width: `${uploadPercent}%` }} />
        </div>
        <p className="kc-upload-status">Uploading… {uploadPercent}%</p>
      </div>
    );
  }

  if (phase === 'confirming') {
    return (
      <div className="kc-upload-widget">
        <div className="spinner" />
        <p className="kc-upload-status">Starting processing…</p>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="kc-upload-widget">
        <p className="kc-upload-status kc-upload-status--error">⚠️ {errorMsg}</p>
        {transcriptionInputs}
        <button className="btn-sm btn-accent" onClick={() => fileInputRef.current?.click()}>Retry</button>
        <input ref={fileInputRef} type="file" accept="video/*" hidden onChange={onFileInputChange} />
      </div>
    );
  }

  return (
    <div className="kc-upload-widget">
      <div className="kc-video-placeholder">🎬</div>

      <div className="opt-row-group">
        <label className={`opt-row ${source === 'upload' ? 'opt-row--active' : ''}`}>
          <input type="radio" name="kc-video-source" checked={source === 'upload'} onChange={() => setSource('upload')} />
          <span className="opt-row-body"><span className="opt-row-title">Upload a new video</span></span>
        </label>
        <label className={`opt-row ${source === 'existing' ? 'opt-row--active' : ''}`}>
          <input type="radio" name="kc-video-source" checked={source === 'existing'} onChange={() => setSource('existing')} />
          <span className="opt-row-body"><span className="opt-row-title">Use an already-uploaded video</span></span>
        </label>
      </div>

      {source === 'upload' && (
        <>
          {transcriptionInputs}
          <button className="btn-sm btn-accent" onClick={() => fileInputRef.current?.click()}>Upload Video</button>
          <input ref={fileInputRef} type="file" accept="video/*" hidden onChange={onFileInputChange} />
        </>
      )}

      {source === 'existing' && (
        <>
          <ExistingVideoPicker
            projectId={projectId}
            selectedId={selectedVideo?.id || null}
            onSelect={video => { setSelectedVideo(video); setReuseTranscript(true); }}
          />
          {selectedVideo?.has_transcript && (
            <div className="modal-field">
              <label>Transcription</label>
              <label className={`opt-row ${reuseTranscript ? 'opt-row--active' : ''}`}>
                <input type="radio" name="kc-reuse-transcript" checked={reuseTranscript} onChange={() => setReuseTranscript(true)} />
                <span className="opt-row-body">
                  <span className="opt-row-title">Reuse existing transcript</span>
                  <span className="opt-row-desc">Faster. Skips re-running Whisper on this video.</span>
                </span>
              </label>
              <label className={`opt-row ${!reuseTranscript ? 'opt-row--active' : ''}`}>
                <input type="radio" name="kc-reuse-transcript" checked={!reuseTranscript} onChange={() => setReuseTranscript(false)} />
                <span className="opt-row-body">
                  <span className="opt-row-title">Transcribe again</span>
                  <span className="opt-row-desc">Slower. Re-runs Whisper on this video's audio.</span>
                </span>
              </label>
            </div>
          )}
          {selectedVideo && !selectedVideo.has_transcript && transcriptionInputs}
          <button className="btn-sm btn-accent" disabled={!selectedVideo} onClick={handleUseExisting}>Use This Video</button>
        </>
      )}
    </div>
  );
};

export default KineticVideoUpload;
